import type { FC } from 'react'

import { AuthClient } from '@skytech/auth'
import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { Swipeable, Toggle } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import AccountCircleIcon from 'mdi-react/AccountCircleIcon'
import AccountDetailsIcon from 'mdi-react/AccountDetailsIcon'
import BellRingIcon from 'mdi-react/BellRingIcon'
import MessagesIcon from 'mdi-react/BullhornIcon'
import CachedIcon from 'mdi-react/CachedIcon'
import CartIcon from 'mdi-react/CartIcon'
import ShopIcon from 'mdi-react/CellphoneIcon'
import DashboardIcon from 'mdi-react/ChartBarIcon'
import MonitorIcon from 'mdi-react/MonitorIcon'
import LogOutIcon from 'mdi-react/PowerIcon'
import GuidelinesIcon from 'mdi-react/ScriptTextIcon'
import UpgradeIcon from 'mdi-react/TagPlusIcon'
import ServiceIcon from 'mdi-react/WrenchIcon'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { twJoin, twMerge } from 'tailwind-merge'

import { consentResources } from '@/api/consent'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { Translation } from '@/components/i18n/Translation'
import { setupMessages } from '@/components/messages/messagesActions'
import { LOCAL_CART_STORAGE_KEY } from '@/contexts/cart/cart-storage'
import useSetup from '@/hooks/setup'
import { useProfile } from '@/hooks/useProfile'
import useUser, { useEditTranslationsPrivilege, useIsKPMG, useIsThreeStepIT } from '@/hooks/user'
import { APP_ROUTES } from '@/util/appRoutes'
import { phoneNumber } from '@/util/formatters/text'

import { formatNotificationCount } from './NotificationBell'

import './style/navigation.scss'

export const Navigation: FC = () => {
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)
  const dispatch = useDispatch()
  const { profile, isProfileLoading } = useProfile()

  const {
    whiteLabel,
    locale,
    isEditTranslationsMode,
    setIsEditTranslationsMode,
    isMobileNavBar,
    setIsMobileNavBar,
    setIsActiveEmployeeConsentModalOpen,
  } = useAppContext()
  const editTranslationsPrivilege = useEditTranslationsPrivilege()
  const setup = useSetup()
  const user = useUser()
  const isKPMG = useIsKPMG()
  const isThreeStepIT = useIsThreeStepIT()

  const { data: activeConsent } = useQuery(
    queries.activeConsent(),
    consentResources.getActiveConsents,
    {
      enabled: Boolean(user),
    }
  )

  const isShopVisible = setup?.has_ecom_policy
  const hasResale = setup?.has_resale

  const displayMessages = () => {
    setIsMobileNavBar(false)
    // @ts-ignore
    dispatch(setupMessages({ setup, user, locale }))
  }

  const onLogoutHandler = () => {
    localStorage.removeItem(LOCAL_CART_STORAGE_KEY)

    AuthClient.logOut()
  }

  const hasDevicePolicyId = Boolean(user?.devicePolicyId)

  if (!profile || isProfileLoading) {
    return null
  }

  const notificationCount = formatNotificationCount(profile.activitiesCount || 0)

  return (
    <div
      id="navigation"
      className={twMerge(
        whiteLabel,
        'absolute flex flex-col transition-all lg:static lg:flex lg:h-full lg:w-72 xl:w-96',
        isMobileNavBar ? 'mobile:w-96 z-[51] h-full w-full lg:z-20' : 'z-20 w-0'
      )}
    >
      <Swipeable
        id="menu"
        className={`${whiteLabel} flex w-full flex-1 flex-col justify-between overflow-auto shadow-xl`}
        onSwipedLeft={() => setIsMobileNavBar(false)}
      >
        <div>
          {user && (
            <div
              className={twJoin(
                'mt-2 flex flex-row items-start justify-start gap-1 px-2 sm:mt-4 lg:hidden',
                isThreeStepIT ? 'fill-threeStepIT-seaMistPrimary' : undefined
              )}
            >
              <AccountCircleIcon
                className="h-14 w-14 sm:h-16 sm:w-16"
                color={isThreeStepIT ? 'fill-threeStepIT-seaMistPrimary' : undefined}
              />

              <div className="flex flex-col items-start text-base">
                {user?.name}
                <div className="text-xs opacity-80">{phoneNumber(user.code)}</div>
                <div className="text-xs opacity-80">
                  {user.departmentCode} - {user.departmentName}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col">
            {/* Notifications */}
            {profile && profile.activitiesCount !== 0 && (
              <Link
                to={APP_ROUTES.activities}
                className="menu-option-item !flex items-center"
                onClick={() => setIsMobileNavBar(false)}
              >
                <BellRingIcon />
                <span className="flex w-full items-center justify-between gap-1">
                  <Translation id="Notifications" />
                  <span
                    className={twJoin(
                      'h-7 w-7 rounded-full py-2 text-center text-xs leading-none text-white',
                      whiteLabel === 'threeStepIT'
                        ? 'bg-threeStepIT-failure'
                        : whiteLabel === 'telia'
                          ? 'bg-telia-red'
                          : 'bg-red'
                    )}
                  >
                    {notificationCount}
                  </span>
                </span>
              </Link>
            )}

            {/* Dashboard */}
            {Boolean(profile.allowSubscriptions) && (
              <Link
                to="/dashboard"
                className="menu-option-item"
                onClick={() => setIsMobileNavBar(false)}
              >
                <DashboardIcon />
                <Translation id="Dashboard" />
              </Link>
            )}

            {/* Messages */}
            <button className="menu-option-item" onClick={displayMessages}>
              <MessagesIcon />
              <Translation id="Meldinger" />
            </button>

            {/* Shop (apply for new phone) */}
            {hasDevicePolicyId && (
              <Link
                to="/shop"
                className="menu-option-item"
                onClick={() => setIsMobileNavBar(false)}
              >
                <ShopIcon />
                <Translation id="Søk om ny mobil" />
              </Link>
            )}

            {/* Upgrade (mobile services) */}
            {Boolean(profile.allowSubscriptions) && (
              <Link
                to="/subscription-services"
                className="menu-option-item"
                onClick={() => setIsMobileNavBar(false)}
              >
                <UpgradeIcon />
                <Translation id="Private kjøp" />
              </Link>
            )}

            {/* Ecom policy (shop) */}
            {isShopVisible && (
              <Link
                to={APP_ROUTES.ecomPolicyList}
                className="menu-option-item"
                onClick={() => setIsMobileNavBar(false)}
              >
                <CartIcon />
                <Translation id="Kjøp av utstyr" />
              </Link>
            )}

            {/* Repair (Home office) */}
            {isKPMG && (
              <Link
                to={APP_ROUTES.hardware}
                className="menu-option-item"
                onClick={() => setIsMobileNavBar(false)}
              >
                <MonitorIcon />
                <Translation id="Hjemmekontor" />
              </Link>
            )}

            {/* Resale */}
            {hasResale && (
              <Link
                to={APP_ROUTES.resale}
                className="menu-option-item resale"
                onClick={() => setIsMobileNavBar(false)}
              >
                <CachedIcon />
                <Translation id="Bruktutstyr" />
              </Link>
            )}

            {/* My assets */}
            <Link
              to={APP_ROUTES.myAssets}
              className="menu-option-item"
              onClick={() => setIsMobileNavBar(false)}
            >
              <ServiceIcon />
              <Translation id="Mitt utstyr" />
            </Link>

            {/* Guidelines */}
            <Link
              to={APP_ROUTES.guidelines}
              className="menu-option-item"
              onClick={() => setIsMobileNavBar(false)}
            >
              <GuidelinesIcon />
              <Translation id="Mine retningslinjer" />
            </Link>

            {/* Employee consent */}
            {Boolean(activeConsent?.isConsentGiven) && (
              <button
                className="menu-option-item"
                onClick={() => {
                  setIsActiveEmployeeConsentModalOpen(true)
                  setIsMobileNavBar(false)
                }}
              >
                <AccountDetailsIcon />

                <Translation id="Employee consent" />
              </button>
            )}

            <div className="menu-option">
              {['CAN_EDIT_OWN_LANG', 'CAN_EDIT_ALL_LANG'].includes(editTranslationsPrivilege) && (
                <Toggle
                  id="edit-translations-mode"
                  label={<Translation id="Edit translations mode" />}
                  checked={isEditTranslationsMode}
                  marginBottom={16}
                  marginTop={16}
                  onChange={() => setIsEditTranslationsMode(!isEditTranslationsMode)}
                />
              )}
            </div>

            {environmentBadgeLabel && (
              <div className="flex justify-center px-4">
                <div className="border-red text-red w-fit rounded-md border-2 border-solid px-1 py-0.5">
                  {environmentBadgeLabel}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <button className="menu-option logout" onClick={onLogoutHandler}>
            <LogOutIcon />
            <Translation id="Logg ut" />
          </button>
        </div>
      </Swipeable>
    </div>
  )
}
